import { Button, Heading, Icon, Modal, ModalBody, ModalFooter, ModalHeader, TextPassage } from '@petsmart-ui/sparky';
import { useEffect } from 'react';

import { trackUpdateAutoshipAddress, trackViewUpdateAutoshipAddressModal } from '@libs/analytics';
import './ConfirmAutoshipDefaultAddressChangeModal.scss';

type ConfirmAutoshipDefaultAddressChangeModalProps = {
    isActive: boolean;
    isFraudValidationNeeded?: boolean;
    onClose: () => void;
    onKeepExistingAddress: () => void;
    onUpdateAddress: () => void;
    loading?: 'keep' | 'update';
};

const componentClassname = 'update-autoship-address-modal';
const KEEP_BUTTON_TEXT = 'No, keep existing address';
const UPDATE_BUTTON_TEXT = 'Yes, update address';
const SITE_SECTION = 'my account | addresses';

export function ConfirmAutoshipDefaultAddressChangeModal({
    isActive,
    isFraudValidationNeeded = false,
    onClose,
    onKeepExistingAddress,
    onUpdateAddress,
    loading,
}: Readonly<ConfirmAutoshipDefaultAddressChangeModalProps>) {
    useEffect(() => {
        if (isActive) trackViewUpdateAutoshipAddressModal(SITE_SECTION);
    }, [isActive]);

    const handleClose = () => {
        trackUpdateAutoshipAddress({
            linkText: 'close',
            siteSection: SITE_SECTION,
        });

        onClose();
    };

    return (
        <Modal dismissible isActive={isActive} className={componentClassname} onClose={handleClose}>
            <ModalHeader className={`${componentClassname}__header`} closeButtonText="close">
                <Heading tagName="h5" className={`${componentClassname}__heading`}>
                    {isFraudValidationNeeded ? 'Update address' : 'Update Autoship address'}
                </Heading>
                <button
                    className="sparky-c-button sparky-c-modal__close-button sparky-c-button--bare sparky-c-button--circle"
                    aria-pressed="true"
                    type="button"
                    onClick={handleClose}
                    data-testid="update-autoship-modal-close-btn"
                >
                    <Icon name="x" />
                </button>
            </ModalHeader>

            <ModalBody className={`${componentClassname}__body`}>
                <TextPassage className={`${componentClassname}__text`}>
                    {isFraudValidationNeeded
                        ? 'Updating your address will save it as a new address in your account. Existing Autoship orders will continue using their current shipping address.'
                        : 'Your previous default address is linked to one or more Autoship orders. Would you like to use your new default address for those Autoship orders?'}
                </TextPassage>
            </ModalBody>

            <ModalFooter
                data-testid="update-autoship-modal-footer"
                className={`${componentClassname}__footer`}
                alignEnd
            >
                <div className={`${componentClassname}__button-group`}>
                    <Button
                        type="button"
                        onClick={() => {
                            trackUpdateAutoshipAddress({
                                linkText: KEEP_BUTTON_TEXT.toLowerCase(),
                                siteSection: SITE_SECTION,
                            });

                            onKeepExistingAddress();
                        }}
                        loading={loading === 'keep' && !isFraudValidationNeeded}
                        disabled={loading === 'keep' && !isFraudValidationNeeded}
                        text={isFraudValidationNeeded ? 'Cancel' : KEEP_BUTTON_TEXT}
                    />
                    <Button
                        variant="primary"
                        onClick={() => {
                            trackUpdateAutoshipAddress({
                                linkText: UPDATE_BUTTON_TEXT.toLowerCase(),
                                siteSection: SITE_SECTION,
                            });
                            onUpdateAddress();
                        }}
                        loading={loading === 'update' || (isFraudValidationNeeded && loading === 'keep')}
                        disabled={loading === 'update' || (isFraudValidationNeeded && loading === 'keep')}
                        text={isFraudValidationNeeded ? 'Save new address' : UPDATE_BUTTON_TEXT}
                    />
                </div>
            </ModalFooter>
        </Modal>
    );
}
